import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Users, BarChart2, Settings, LogOut, Lock, LogIn } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { LanguageSelector } from "./LanguageSelector";
import { useLanguage } from "@/hooks/useLanguage";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";
import { Logo } from "./Logo";
import { useFeatureLimits } from "@/hooks/useFeatureLimits";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface NavigationBarProps {
  session: Session | null;
  showDashboard?: boolean;
}

const NavigationBar = ({ session, showDashboard = true }: NavigationBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [displayName, setDisplayName] = useState<string>("");
  const { isFeatureEnabled, getFeatureDescription } = useFeatureLimits();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<string>("");

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (session?.user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', session.user.id)
          .single();

        if (!error && data) {
          setDisplayName(data.display_name);
        }
      }
    };

    fetchUserProfile();
  }, [session]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const navigation = [
    {
      name: t('dashboard.navigation.stories'),
      icon: BookOpen,
      href: '/dashboard',
    },
    {
      name: t('dashboard.navigation.characters'),
      icon: Users,
      href: '/dashboard/characters',
      featureKey: 'characters_access',
    },
    {
      name: t('dashboard.navigation.stats'),
      icon: BarChart2,
      href: '/dashboard/stats',
      featureKey: 'stats_access',
    },
    {
      name: t('navigation.profile'),
      icon: Settings,
      href: '/dashboard/more',
    },
  ];

  const handleNavigation = (item: typeof navigation[0]) => {
    if (item.featureKey && !isFeatureEnabled(item.featureKey, 'limit')) {
      setSelectedFeature(item.featureKey);
      setIsDialogOpen(true);
      return;
    }
    navigate(item.href);
  };

  const handleUpgradeClick = () => {
    setIsDialogOpen(false);
    navigate("/dashboard/more");
  };

  const handleSignIn = () => {
    navigate("/auth");
  };

  return (
    <>
      <header className="relative bg-black/50 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Logo size={isMobile ? "md" : "md"} className="text-white" />
            </div>
            <div className="flex items-center gap-2">
              {session ? (
                // Display full navigation when user is logged in
                <>
                  {!isMobile && (
                    <>
                      {navigation.map((item) => {
                        const isActive = location.pathname === item.href;
                        const isLocked = item.featureKey && !isFeatureEnabled(item.featureKey, 'limit');

                        return (
                          <TooltipProvider key={item.name}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  onClick={() => handleNavigation(item)}
                                  variant={"ghost"}
                                  className={`gap-2 ${
                                    isActive ? 'text-[#7E69AB]' : 'font-semibold text-white hover:text-white hover:bg-white/10'
                                  }`}
                                >
                                  <item.icon className="h-4 w-4" />
                                  {item.name}
                                  {isLocked && <Lock className="h-4 w-4" />}
                                </Button>
                              </TooltipTrigger>
                              {isLocked && (
                                <TooltipContent>
                                  <p>{getFeatureDescription(item.featureKey)}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })}
                    </>
                  )}
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size={isMobile ? "icon" : "default"}
                    className={`gap-2 font-semibold text-white hover:text-white hover:bg-white/10`}
                  >
                    <LogOut style={isMobile ? {width: '24px', height: '24px', minWidth: '24px'} : undefined} />
                    {!isMobile && t('navigation.signOut')}
                  </Button>
                </>
              ) : (
                // Display only sign in button when user is not logged in
                <div className="relative z-20 w-full flex justify-end items-center gap-2">
                  <Button
                    onClick={handleSignIn}
                    variant="secondary"
                    size={isMobile ? "icon" : "default"}
                    className="bg-white/20 border-2 border-white text-white hover:bg-white/30 font-semibold transition-all"
                  >
                    <LogIn style={isMobile ? {width: '24px', height: '24px', minWidth: '24px'} : undefined} />
                    {!isMobile && t('landing.hero.signIn')}
                    
                    {/* {t('landing.hero.signIn')} */}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('settings.subscription.upgradeRequired')}</DialogTitle>
            <DialogDescription>
            {t('settings.subscription.upgradeForAccess')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-black">
            <Lock className="w-5 h-5 text-primary" />
            <span>{t('navigation.basicSubscriptionRequired')}</span>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              onClick={() => {
                setIsDialogOpen(false);
                navigate("/dashboard/more");
              }}
              className="w-full sm:w-auto"
            >
              {t('settings.subscription.upgradeNow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NavigationBar;
